import type { MouseEvent, ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
    ArrowSquareOutIcon,
    ClockCountdownIcon,
    ClockIcon,
    FileVideoIcon,
} from '@phosphor-icons/react';
import {
    formatDate,
    formatEventTimeCompact,
    formatRelativeTimeI18n,
    isVideoTimeRange,
} from '@/utils/date';
import { stripFileExtension } from '@/utils/file-name';
import { preventMiddleClickAutoscroll } from '@/utils/mouse';
import { useRelativeTimeTick } from '@/hooks';
import type { ActionMenuItem, ThumbnailQuickViewProps } from '@/components';
import {
    Badge,
    Card,
    ConfidenceBadge,
    EntityActionsMenu,
    type CardMetadataItem,
} from '@/components';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { SourceType } from '@/types/source';
import { SecurityCameraIconMirrored } from '@/utils/icons';
import BorderGlow from '@/components/BorderGlow';
import {
    BORDER_GLOW_CHROMELESS,
    NEW_ALERT_BORDER_GLOW,
} from './newAlertEffects';

const FEED_CARD_INITIAL = { opacity: 0, scale: 0.95 };
const FEED_CARD_ANIMATE = { opacity: 1, scale: 1 };
const FEED_CARD_TRANSITION = { duration: 0.15, ease: 'easeOut' as const };

export interface EventFeedCardProps {
    thumbnailUrl: string;
    title: ReactNode;
    description?: string;
    licensePlate?: string;
    isScene?: boolean;
    timestamp: number;
    timeRange?: { start: number; end: number };
    timeItem?: CardMetadataItem;
    cameraName?: string;
    sourceType?: SourceType;
    confidence?: number;
    useRelativeTime?: boolean;
    onClick?: (event: MouseEvent<HTMLDivElement>) => void;
    onAuxClick?: (event: MouseEvent<HTMLDivElement>) => void;
    onRef?: (el: HTMLDivElement | null) => void;
    isWatching?: boolean;
    isPipWatching?: boolean;
    watchingLabel?: string;
    onNavigate?: () => void;
    onQuickView?: (event: MouseEvent) => void;
    actionMenuItems?: ActionMenuItem[];
    animated?: boolean;
    isNew?: boolean;
}

export function EventFeedCard({
    thumbnailUrl,
    title,
    description,
    licensePlate,
    isScene = false,
    timestamp,
    timeRange,
    timeItem,
    cameraName,
    sourceType,
    confidence,
    useRelativeTime = false,
    onClick,
    onAuxClick,
    onRef,
    isWatching,
    isPipWatching,
    watchingLabel,
    onNavigate,
    onQuickView,
    actionMenuItems,
    animated = false,
    isNew = false,
}: EventFeedCardProps) {
    const { t } = useTranslation(I18nNamespace.COMMON);
    const SourceIcon =
        sourceType === SourceType.CAMERA
            ? SecurityCameraIconMirrored
            : FileVideoIcon;
    const isVideoTime = isVideoTimeRange(sourceType) && timeRange;
    const nowMs = useRelativeTimeTick(
        !timeItem && (useRelativeTime || !isVideoTime),
    );
    const timeText =
        useRelativeTime || !isVideoTime
            ? formatRelativeTimeI18n(timestamp, nowMs || undefined)
            : formatEventTimeCompact(timeRange.start, timeRange.end);
    const TimeIcon =
        useRelativeTime || !isVideoTime ? ClockIcon : ClockCountdownIcon;
    const metadata: CardMetadataItem[] = [
        timeItem ?? {
            icon: TimeIcon,
            text: timeText,
            title:
                useRelativeTime || !isVideoTime
                    ? formatDate(timestamp)
                    : undefined,
        },
        ...(cameraName
            ? [
                  {
                      icon: SourceIcon,
                      text: stripFileExtension(cameraName),
                  },
              ]
            : []),
    ];

    const quickView: ThumbnailQuickViewProps | undefined =
        isWatching || isPipWatching
            ? {
                  state: 'watching',
                  ariaLabel: watchingLabel ?? t('button.watching'),
              }
            : !isWatching && onQuickView
              ? {
                    state: 'idle',
                    onClick: onQuickView,
                    ariaLabel: t('button.quickView'),
                }
              : undefined;

    const handleMouseDown = onAuxClick && preventMiddleClickAutoscroll;
    const shouldReduceMotion = useReducedMotion();

    const highlightNewAlert = isNew && !shouldReduceMotion;

    const withBorderGlow = (content: ReactNode) => (
        <BorderGlow
            {...NEW_ALERT_BORDER_GLOW}
            colors={[...NEW_ALERT_BORDER_GLOW.colors]}
            className={BORDER_GLOW_CHROMELESS}
            animated={highlightNewAlert}
        >
            {content}
        </BorderGlow>
    );

    const tileContent = (
        <Card
            orientation="horizontal"
            subtitle={title}
            title={description}
            metadata={metadata}
            className="group cursor-pointer rounded-lg transition-colors duration-200 hover:bg-grey-550"
            thumbnailImageSrc={thumbnailUrl}
            thumbnailImageUsesAuth
            belowTitleSlot={
                licensePlate ? (
                    <Badge value={licensePlate} variant="license-plate" />
                ) : undefined
            }
            topLeftSlot={
                isScene || confidence !== undefined ? (
                    <div className="flex items-center gap-1">
                        {confidence !== undefined && (
                            <ConfidenceBadge
                                value={confidence}
                                glass
                                compact
                                className="pointer-events-none h-[22px] min-h-[22px] gap-1 px-1.5 py-0.5 backdrop-blur-[20px]"
                            />
                        )}
                        {isScene && (
                            <Badge variant="neutral-glass">
                                {t('scene.badge', {
                                    defaultValue: 'Scene',
                                })}
                            </Badge>
                        )}
                    </div>
                ) : undefined
            }
            thumbnailQuickView={quickView}
            cardTopRightSlot={
                actionMenuItems && actionMenuItems.length > 0 ? (
                    <EntityActionsMenu
                        items={actionMenuItems}
                        wrapperClassName="flex-shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                        triggerButtonClassName="rounded bg-grey-700/80 p-1 hover:bg-grey-500"
                        triggerIconClassName="size-4 text-grey-200"
                        triggerIconOrientation="horizontal"
                    />
                ) : onNavigate ? (
                    <button
                        onClick={(event) => {
                            event.stopPropagation();
                            onNavigate();
                        }}
                        className="rounded bg-grey-700/80 p-1 opacity-0 transition-opacity hover:bg-grey-500 group-hover:opacity-100"
                    >
                        <ArrowSquareOutIcon className="size-4 text-grey-200" />
                    </button>
                ) : null
            }
        />
    );

    if (animated) {
        return (
            <motion.div
                ref={onRef}
                layout
                initial={FEED_CARD_INITIAL}
                animate={FEED_CARD_ANIMATE}
                transition={FEED_CARD_TRANSITION}
                className="relative rounded-lg cursor-pointer"
                onClick={onClick}
                onAuxClick={onAuxClick}
                onMouseDown={handleMouseDown}
            >
                {withBorderGlow(tileContent)}
            </motion.div>
        );
    }

    return (
        <div
            ref={onRef}
            className="relative rounded-lg cursor-pointer"
            onClick={onClick}
            onAuxClick={onAuxClick}
            onMouseDown={handleMouseDown}
        >
            {withBorderGlow(tileContent)}
        </div>
    );
}
